<template>
    <div class="gh-page gh-page--training">
        <gh-header :affiliate="affiliate"/>

        <gh-banner
            :course="course"
            :affiliate="affiliate"
            iframe-video-id="398983875"
            iframeBackgroundSrc="/images/credential/Cirecie-Screenshot-vid.png">
            <template v-slot:sectionHeading>
                <h1 class="gh-title">Disaster Mental Health Counselling</h1>
            </template>
            <template v-slot:sectionDescription>
                <p class="gh-intro">The gold standard program in Disaster Mental Health Counselling. This digitally badged Micro-credential Course is led by international expert, Prof. Cirecie West-Olatunji, Ph.D. (Director of the XULA Center for Traumatic Stress Research). It’s a <b>31-hour deep-dive program</b> that will teach you best-practice strategies, clinical skills and cultural insights to effectively serve in disaster-affected areas – anywhere in the world. </p>
            </template>
        </gh-banner>

        <gh-cta :affiliate="affiliate"/>

        <gh-features :features="features"/>

        <gh-overview ref="overview"
            :overview-features="overviewFeatures"
            :overview-outlines="overviewOutlines">
            <template v-slot:sectionSubHeading>
                DMHC Micro-credential Course
            </template>
             <template v-slot:sectionDescription>
                <p class="gh-intro">This comprehensive, 31-hour program will enhance your clinical skills, cultural capability and evidence-based knowledge to serve in disaster-affected areas. As a participant, you will learn the history, theories, and interventions associated with disaster mental health counselling, with a special focus on working with culturally diverse individuals and vulnerable client populations. The program will guide you through the full spectrum of disaster mental health counselling service provision: from learning the requisite theories and background for clinical practice, through to preparing for deployment, arriving and working effectively with disaster-affected communities, and finally, returning home. You will cover topics such as types of disasters and reactions, entering communities, counsellor bias, historical trauma, deployment site arrival, assessment and triage, working with multidisciplinary teams, clinical supervision, interventions in the trenches, compassion fatigue, and much more.</p>
                <br>
                <p class="gh-intro">Throughout the course you will also be exposed to real-life stories that illustrate what you are learning. These include case studies, photo montages, video/audio testimonials from professionals who have served in disaster areas, and a wealth of insights and anecdotes from your course facilitator, Dr. West-Olatunji.</p>
            </template>
        </gh-overview>

        <gh-cta :affiliate="affiliate"/>

        <gh-presenter ref="presenter">
            <template v-slot:sectionHeading>
                 Learn from international expert, <br><strong>Dr. Cirecie West-Olatunji</strong>
            </template>
            <template v-slot:presenterImage>
                <img src="/images/credential/West-Olatunji_new-photo.png" alt="Dr. West Olatunji profile photo" class="gh-presenter-photo">
            </template>
            <template v-slot:bio>
                 Cirecie A. West-Olatunji, Ph.D., serves as Professor in the Counseling program at Xavier University of Louisiana (United States) and director of the XULA Center for Traumatic Stress Research. She is a past president of the American Counseling Association (ACA) and the Association for Multicultural Counseling & Development (AMCD). Dr. West-Olatunji has initiated several clinical research projects that focus on culture-centred community collaborations designed to address issues rooted in systemic oppression, such as transgenerational trauma and traumatic stress. Dr. West-Olatunji has conducted commissioned research under the auspices of the: National Science Foundation, American Counseling Association, Kellogg Foundation, federal Witness Assistance Program, Spencer Foundation, the American Educational Research Association, and the African-American Success Foundation. Her publications include two co-authored books: Counseling African Americans and Future Vision, Present Work, several book chapters, and over 50 articles in peer-reviewed journals. In addition to national presentations, Dr. West-Olatunji has delivered research papers in throughout Africa, Europe, the Pacific Rim, and the Americas. Additionally, she has provided consultation in a PBS initiative to create a children's television show <span class="gh-bio-right">focusing on diversity through KCET-TV in Los Angeles, CA ("Puzzle Place"). Dr. West-Olatunji has coordinated disaster mental health outreach projects in post-Katrina New Orleans, southern Africa to investigate illness as disaster to work with HIV-AIDS infected and affected individuals in South Africa and Botswana, and in post-earthquake Haiti (Port-au-Prince area). Dr. West-Olatunji has also led a People-to-People delegation to India with a focus on Disaster Mental Health Counselling. Dr. West-Olatunji has developed a model for culture-centred disaster mental health counselling that she has delivered to mental health professionals in Thailand, India, England, Ireland, China, and Haiti.</span>
            </template>
            <template v-slot:cta>
                <a href="https://gallery.mailchimp.com/f0f905dbc37175a00c83da5e0/files/26f67805-da66-439b-aace-c16958157b13/List_of_Publications.pdf" target="_blank">Click here to view a full list of Dr. West-Olatunji's publications & presentations</a>
            </template>
        </gh-presenter>

        <gh-resources
            ref="whatYouLearn"
            :resource-features="resourceFeatures"
            :resources="resources">
            <template v-slot:sectionSubHeading>
                 The 31-hour Disaster Mental Health Counselling MHA Micro-credential Course is delivered online. As the course is 100% self-paced and accessible on-demand (24/7), you can start at any time and set your own pace. Learning resources and support tools include:
            </template>
        </gh-resources>

        <gh-facts/>

        <gh-cpd
            ref="cpdOpd"
            certificate-image="certificate-dmhc.png">
            <template v-slot:sectionDescription>
                <p class="gh-intro">When you complete this course, you’ll be issued with an <b>MHA Disaster Mental Health Counselling Digital Badge</b> and a <b>CPD Certificate of Attainment</b>. A Digital Badge is a form of Digital Credential (also known as ‘micro-credential’) that can be verified/validated online. A digital badge signals your achievement to potential employers and stakeholders, as they are able to verify your learning/skill acquisition outcomes in real-time, over the web. </p>

                <p class="gh-intro">In addition to your MHA DMHC Digital Badge, you’ll receive a downloadable CPD Certificate of Attainment highlighting the 31 CPD hours you’ve completed. </p>

                <p class="gh-intro">Some of the mental health professionals who may be able to accrue CPD hours include:</p>

                <ul class="gh-list">
                    <li v-for="(item, index) in cpdIncludes" :key="index" class="gh-list-item">
                        <i class="el-icon-success gh-list-icon"></i>
                        <span class="gh-intro gh-list-label">{{ item }}</span>
                    </li>
                </ul>

                <p class="gh-intro" v-if="affiliate && affiliate.name === 'ACA'" key="aca">ACA members accrue 10 OPD points upon completion of this course. For more details on CPD hours/points with other Associations, please contact your Association.</p>
                <p class="gh-intro" v-else key="general">For more details on CPD/OPD hours/points, please contact your association.</p>
            </template>
        </gh-cpd>

        <gh-cta :affiliate="affiliate"/>

        <gh-enrol ref="enrol" access="enrol" :affiliate="affiliate" :profession-id="professionId" :course="course" base-path="/credential/dmhc"/>

        <gh-faqs :faqs="faqs"/>

        <gh-footer/>
    </div>
</template>

<script>
    import UX from '@/utils/ux';

    export default {
        components: {
            ghHeader: () => import(/* webpackChunkName: "credentialCourse" */ '@/modules/credentialCourse/components/Header.vue'),
            ghBanner: () => import(/* webpackChunkName: "credentialCourse" */ '@/modules/credentialCourse/components/Banner.vue'),
            ghCta: () => import(/* webpackChunkName: "credentialCourse" */ '@/modules/credentialCourse/components/Cta.vue'),
            ghFeatures: () => import(/* webpackChunkName: "credentialCourse" */ '@/modules/credentialCourse/components/Features.vue'),
            ghOverview: () => import(/* webpackChunkName: "credentialCourse" */ '@/modules/credentialCourse/components/Overview.vue'),
            ghPresenter: () => import(/* webpackChunkName: "credentialCourse" */ '@/modules/credentialCourse/components/Presenter.vue'),
            ghResources: () => import(/* webpackChunkName: "credentialCourse" */ '@/modules/credentialCourse/components/Resources.vue'),
            ghFacts: () => import(/* webpackChunkName: "credentialCourse" */ '@/modules/credentialCourse/components/Facts.vue'),
            ghCpd: () => import(/* webpackChunkName: "credentialCourse" */ '@/modules/credentialCourse/components/Cpd.vue'),
            ghEnrol: () => import(/* webpackChunkName: "credentialCourse" */ '@/modules/credentialCourse/components/Enrol.vue'),
            ghFaqs: () => import(/* webpackChunkName: "credentialCourse" */ '@/modules/credentialCourse/components/Faqs.vue'),
            ghFooter: () => import(/* webpackChunkName: "credentialCourse" */ '@/views/public/footer/index.vue')
        },

        data() {
            return {
                timeout: undefined,
                dialogVisible: false,
                course: {},
                features: [
                    {
                        title: 'WATCH 24 VIDEO LECTURES',
                        text: 'In these highly engaging lectures, Dr. West-Olatunji shares 25+ years of international experience in the field, guiding you through the stages of disaster counselling provision, from preparation through to deployment and returning home.',
                        icon: 'overview-icons-01-livevideo.svg'
                    },
                    {
                        title: 'LEARN ONLINE, ON-DEMAND, 24/7',
                        text: 'The program is 100% self-paced and available on-demand. You can start at any time, complete units progressively, and enjoy the convenience of accessing all learning resources from the comfort of home.',
                        icon: 'overview-icons-02-online.svg'
                    },
                    {
                        title: 'COLLABORATE WITH PROFESSIONAL PEERS',
                        text: 'Connect with course participants from all over the world via an online peer-driven forum, and develop long-term connections with other mental health professionals and community workers.',
                        icon: 'overview-icons-03-collaborative.svg'
                    },
                    {
                        title: 'EARN A DIGITAL CREDENTIAL',
                        text: 'Upon completion, you’ll be issued with an internationally-verifiable MHA Digital Badge and join a select group of credentialed DMHC professionals. You’ll also receive a downloadable CPD Certificate of Attainment.',
                        icon: 'overview-icons-04-digitalcredential.svg'
                    }
                ],
                overviewFeatures: [
                    'Increased knowledge about the history of disaster counselling and the mental health issues that result from experiencing disasters',
                    'Increased knowledge about assessment and triage for disaster-affected clients',
                    'Increased self-awareness about cultural considerations when assessing and treating affected individuals',
                    'Augmented knowledge about working in interdisciplinary disaster response teams',
                    'Enhanced knowledge about models of disaster counselling',
                    'Enhanced knowledge about culture-centred disaster mental health counselling (C-DMHC) interventions',
                    'Increased knowledge about the use of a psychosocial approach to disaster mental health',
                    'Increased knowledge about daily routine of culture-centred disaster mental health counselling',
                    'Augmented knowledge about traumatic stress for disaster-affected populations',
                    'Augmented knowledge about prevention of compassion fatigue',
                    'Increased self-awareness about cultural considerations when assessing and treating affected individuals',
                    'Enhanced knowledge about social justice and advocacy in disaster mental health counselling'
                ],

                overviewOutlines: [
                    {
                        id: 1,
                        title: 'Introduction to Disaster Mental Health Counselling',
                        text: 'This module provides an overview of the key terms and concepts associated with disaster mental health counselling. Special attention is paid to working with culturally diverse individuals and other vulnerable client populations. Topics include the goals and mission of culture-centred disaster mental health counselling (C-DMHC) as well as the types of clients typically served in C-DMHC work. A review of the limitations of current DMHC approaches is provided and a rationale for C-DMHC is discussed.',
                        includes: [
                            'Goals and Mission of Disaster Counselling',
                            'Stages & Characteristics of Disasters',
                            'Types of Disasters & Reactions',
                            'Disaster Counselling Training Models',
                            'Culture-centred Disaster Mental Health Counselling',
                            'Where To Go From Here'
                        ]
                    },
                    {
                        id: 2,
                        title: 'Preparing to Serve (Pre-impact)',
                        text: 'This module presents an overview of how mental health professionals prepare for deployment prior to entering a disaster-affected area. Special attention is given to working with culturally diverse individuals and other vulnerable client populations. Topics include increasing awareness of counsellor bias, understanding environmental stressors and strengths, and historical trauma and traumatic stress. A review of the limitations of current DMHC approaches is provided and a rationale for C-DMHC is discussed.',
                        includes: [
                            'Entering Communities',
                            'Counsellor Bias',
                            'Environmental Stressors & Strengths',
                            'Historical Trauma',
                            'C-DMHC: Goals and Mission',
                            'Where To Go From Here'
                        ]
                    },
                    {
                        id: 3,
                        title: 'Working with Disaster-affected Clients',
                        text: 'This module provides an overview of the on-the-ground activities associated with disaster mental health counselling. Special attention is paid to working with culturally diverse individuals and other vulnerable client populations. Topics include what to do upon arrival at the deployment site as well as how to engage in assessment and triage of disaster-affected clients. A review of the do’s and don’ts of working in interdisciplinary teams is also provided, along with an explanation of clinical supervision issues during deployment. Finally, Dr West-Olatunji engages a comprehensive discussion of the interventions used with the C-DMHC approach.',
                        includes: [
                            'Deployment Site Arrival',
                            'Assessment and Triage',
                            'Working in Interdisciplinary Teams',
                            'Clinical Supervision',
                            'C-DMHC Interventions',
                            'Where To Go From Here'
                        ]
                    },
                    {
                        id: 4,
                        title: 'Moving Toward Recovery (Post-impact)',
                        text: 'This final module provides an overview of the key terms and concepts associated with disaster mental health counselling. Special attention is paid to working with culturally diverse individuals and other vulnerable client populations. Topics include the goals and mission of culture-centred disaster mental health counselling (C-DMHC), as well as the types of clients typically served in C-DMHC work. A review of the limitations of current DMHC approaches is provided and a rationale for C-DMHC is discussed',
                        includes: [
                            'The Routine',
                            'Taking Breaks',
                            'Interventions in the Trenches',
                            'Compassion Fatigue',
                            'Returning Home',
                            'Where To Go From Here'
                        ]
                    }
                ],
                resources: [
                    {
                        id: 1,
                        title: 'Your Course Companion',
                        info: `Upon enrolment, you’ll receive an email confirmation with a downloadable course guide. The guide provides a full description of each of the course’s 4 modules and 24 units, information on assessments, a recommended workflow to progress with your learning, FAQs, contacts and more.`
                    },
                    {
                        id: 2,
                        title: 'Real-life Stories',
                        info: `This course was developed over a 25-year span of practical experiences in disaster mental health counselling. The concepts you’ll learn are explained through the lens of real-life scenarios. As you progress through each unit, you’ll examine case studies, listen to testimonials from deployed professionals, view photos from disaster-affected areas, and learn the ‘ins and outs’ of supporting disaster-affected clients while respecting their culturally-unique needs.  `
                    },
                    {
                        id: 3,
                        title: 'In-depth Knowledge',
                        info: `Your list of core readings includes 24 hand-picked journal articles, examining research and first-hand experiences in disaster counselling, trauma, community mental health, and more. You’ll also be referred to dozens of supplemental learning resources to help strengthen your knowledge and understanding. These resources include peer-reviewed journal articles, video demonstrations, animations, practice guidelines, government reports, and more.`
                    },
                    {
                        id: 4,
                        title: 'Peer-to-peer Interaction',
                        info: `Our private Facebook Group provides the ideal platform to interact with other course participants, share insights, ask questions, access further resources, and much more. We monitor the group closely and use it to keep you posted on industry developments and outreach opportunities.`
                    }
                ],
                resourceFeatures: [
                    {
                        text: '10 hours of highly engaging video lectures',
                        icon: 'Learning-Icons-01-videolectures.svg'
                    },
                    {
                        text: 'On-demand, 24/7 access to learning materials',
                        icon: 'Learning-Icons-02-ondemand.svg'
                    },
                    {
                        text: 'Dozens of downloadable articles & handouts',
                        icon: 'Learning-Icons-03-downloads.svg'
                    },
                    {
                        text: 'Case studies, illustrations & real-life stories',
                        icon: 'Learning-Icons-04-casestudies.svg'
                    },
                    {
                        text: 'Interactive, peer-driven online forum',
                        icon: 'Learning-Icons-05-interaction.svg'
                    },
                    {
                        text: 'Lifetime, unrestricted access to content',
                        icon: 'Learning-Icons-06-unrestrictedcontent.svg'
                    },
                    {
                        text: 'Practical online assessment platform',
                        icon: 'Learning-Icons-07-assessments.svg'
                    },
                    {
                        text: 'Ongoing support via email/phone',
                        icon: 'Learning-Icons-08-support.svg'
                    }
                ],
            }
        },

        methods: {
            scrollToSection(hash) {
                const value = hash || this.$route.hash

                if (value) {
                    const ref = _.trimStart(value, '#')

                    const vm = this.$refs[ref]

                    vm && UX.scrollTo(vm.$el)
                }
            },

            fetchCourse () {
                return axios.get('/api/v1/credential/courses/dmhc')
                        .then(({ data }) => {
                            this.course = data
                        }).catch(() => this.$router.replace('/'))
            },
        },

        computed: {
            affiliate () {
                return this.$route.meta.affiliate
            },

            cpdIncludes() {
                if (this.affiliate && this.affiliate.name === 'ACA') {
                    return [
                        'Counsellors (ACA OPD Approved)',
                        'Mental Health Nurses',
                        'Community Workers (ACWA Approved)',
                        'Primary Health Care Nurses',
                        'Social Workers (AASW Endorsed)',
                        'General Practitioners (GPs)',
                        'Psychologists (Active CPD)',
                        'Occupational Therapists'
                    ]
                }

                return [
                    'Community Workers (ACWA Approved)',
                    'Mental Health Nurses',
                    'Counsellors (ACA Endorsed)',
                    'Primary Health Care Nurses',
                    'Social Workers (AASW Endorsed)',
                    'General Practitioners (GPs)',
                    'Psychologists (Active CPD)',
                    'Occupational Therapists'
                ]
            },
            professionId () {
                return this.$route.meta.professionId
            },

            device() {
                return this.$store.getters['ui/getDevice'];
            },

            dialogWidth() {
                switch (this.device) {
                    case 'mobile':
                        return '90%'
                    case 'tablet':
                        return '80%'
                    case 'desktop':
                        return '70%'
                    default:
                        return '50%'
                }
            },

            paymentPlanRadioLabels() {
                return  {
                    annual: `<span style="color:#514a9d;"><b>$349/year</b> (save $119)</span>`,
                    monthly: `<span style="color:#514a9d;"><b>$39/month</b> ($468/year – min. 12-month term applies)</span>`
                }
            },

            faqs () {
                const items = [
                    {
                        id: 1,
                        question: 'Can anyone enrol in this course?',
                        answer: '<p>Yes. That being said, this course was specifically designed for qualified mental health professionals wanting to enhance their skills and know-how to effectively work in disaster-affected areas. You will get the most out of it if you already have basic training in mental health and existing knowledge of basic counselling skills and interventions.</p>'
                    },
                    {
                        id: 2,
                        question: `Do I need to be a Mental Health Academy member to access this course?`,
                        answer: `<p>You don't need to be a Mental Health Academy member to enrol in a Micro-credential Course. Access to course materials is available outside of membership.</p> `
                    },
                    {
                        id: 3,
                        question: `I’m an MHA member. How is this course different from what I can access with my membership?`,
                        answer: `<p>MHA Membership Courses (available via our Catalogue) are shorter, specific programs – with most courses ranging from 1 to 4 hours in duration. MHA Micro-credential Courses are designed to provide a deep-dive, content-rich experience (30+ hours of learning) backed up by real-life case studies and illustrations. This particular program is the world’s first and only credential course focusing on a culture-centred approach to disaster mental health counselling.</p> `
                    },
                    {
                        id: 4,
                        question: `Who is the course facilitator?`,
                        answer: `<p>Your course facilitator, Dr. Cirecie A. West-Olatunji, is an internationally-renowned expert in disaster mental health. Dr. West-Olatunji currently lives in New Orleans (United States) where she is a Professor of Counseling at Xavier University of Louisiana and Director of the XULA Center for Traumatic Stress Research. She is also a past president for both the American Counseling Association (ACA) and the Association for Multicultural Counseling & Development (AMCD). Dr. West-Olatunji has coordinated disaster mental health outreach projects in New Orleans, southern Africa, and Haiti (Port-au-Prince area). She has also led a People-to-People delegation to India with a focus on disaster mental health counseling. Her model for culture-centred disaster mental health counselling has been delivered to mental health professionals in Thailand, India, England, Ireland, China, and Haiti.</p> `
                    },
                    {
                        id: 5,
                        question: `What Certification do I get upon completion of this course?`,
                        answer: `<p>When you complete this course, you’ll be issued with an MHA Disaster Mental Health Counselling Digital Badge and a CPD Certificate of Attainment. A Digital Badge is a form of Digital Credential (also known as ‘micro-credential’) that can be verified/validated online. A digital badge signals your achievement to potential employers and stakeholders, as they are able to verify your learning/skill acquisition outcomes in real-time, over the web. In addition to your MHA DMHC Digital Badge, you’ll receive a downloadable CPD Certificate of Attainment, which can also be used as proof of completion and printed (if you would like to display it in your office/workspace).</p> `
                    },
                    {
                        id: 6,
                        question: `What are the assessment requirements for this course?`,
                        answer: `<p>The course includes 24 multiple-choice, online assessments. To advance (i.e. unlock access) to the next module, you must successfully complete the multiple-choice assessments for each of the 6 units within the module you’re currently in. For successful completion of an assessment, you are required to attain a minimum 80% pass rate. As the assessment is competency-based, if you don’t pass in your first try, you will have the opportunity to review, save and re-submit your answers (there are no re-attempt limits). </p> `
                    },
                    {
                        id: 7,
                        question: `What is the duration of the course?`,
                        answer: `<p>The total duration of this course is 31 hours. This is based on a calculation of the number of hours required to read all requisite (i.e. core) articles, watch video lectures and complete the online assessment components. It does not include additional time required to read/watch/access supplemental learning resources.</p> `
                    },
                    {
                        id: 8,
                        question: `How are course content and learning materials delivered?`,
                        answer: `<p>Your course materials will be accessible online via the MHA Learning Portal (i.e. website). Course materials include journal articles, handouts and other downloads, video lectures, and online (multiple-choice) assessments. You will be able to access these materials and learning tools from most computer systems and mobile devices. Technical support will also be available in case you experience any difficulty accessing course resources.</p> `
                    },
                    {
                        id: 9,
                        question: `Can I save course content for future reference?`,
                        answer: `<p>Your course guide, requisite readings, handouts and other supplemental materials can be downloaded and saved to your local computer. Video content and assessments cannot be downloaded; however, they are accessible via the MHA Learning Portal at any time (including after you have successfully completed the course). All enrolled course participants will have lifetime access to requisite course learning materials. </p> `
                    },
                    {
                        id: 10,
                        question: `Can I copy and/or share course learning materials and resources? `,
                        answer: `<p>Courses materials are only to be used by the individual person that enrolled in the course. Sharing your course login/access details and resources constitutes a breach of copyright and may result in cancellation of your enrolment and ineligibility to access Certificates/Credentials.</p> `
                    },
                    {
                        id: 11,
                        question: `What are the system requirements for watching videos?`,
                        answer: `<p>Our video platforms are compatible with all up-to-date browsers (Chrome, Safari, Firefox, Opera, and Edge) and operating systems (Windows, Mac and mobile devices), and most residential internet connections should be more than adequate to watch the videos. If you experience any difficulties accessing video content, you can contact us for support at any time.</p> `
                    },
                    {
                        id: 12,
                        question: `Do I need a Facebook Profile to join the discussion forum (i.e. Facebook Group)?`,
                        answer: `<p>Yes, to access the forum and join in the group discussions, you will need a Facebook Profile. If you do not have a Facebook profile, we recommend setting one up to join the group, as it will enhance your learning experience and provide you with the opportunity to learn from and interact with your peers and course facilitator. While recommended, joining the Facebook Group is <b>not</b> a requirement to access and complete this course. </p> `
                    },
                    {
                        id: 13,
                        question: `I live in a remote area with slow internet. Will I be able to access the lectures and other videos?`,
                        answer: `<p>You may experience difficulties watching videos if your internet speed is slow. We recommend that you contact your Internet Service Provider (ISP) to devise a solution. If that’s not possible, you may wish to consider allocating time for travel, so you can access video content using a more appropriate connection (there will be approximately 10 hours of video content throughout the course). </p> `
                    },
                    {
                        id: 14,
                        question: `What is your course refund policy?`,
                        answer: `<p>Enrolment in this course comes with a 14-day money back guarantee that empowers you to try the program entirely risk-free. If within 14 days of your enrolment and before completion of the first module* you’re not entirely thrilled with the high level content you have access to, just email us to <a href="mailto:help@mentalhealthacademy.com.au">help@mentalhealthacademy.com.au</a> and we'll give you your money back. No questions asked.</p><p><i>*Refund eligibility criteria:</i> You will <u>not</u> be eligible for a full refund if: 1) You have completed all six assessments for Module 1 or, 2) Your cancellation/refund request is emailed 15 (or more) days after your enrolment date. </p>`
                    }
                ]

                if (this.affiliate && this.affiliate.name === 'ACA') {
                    items.unshift( {
                        id: 0,
                        question: `Do I earn OPD points upon completion of this course?`,
                        answer: `<p>Yes, ACA members accrue 10 OPD points upon completion of this course. As proof of completion, you’ll be issued with an MHA Disaster Mental Health Counselling Digital Badge and a CPD Certificate of Attainment.</p>`
                    })
                }

                return items
            }
        },

        created () {
            this.fetchCourse()
        },

        mounted() {
            if (this.$route.hash) {
                this.timeout = setTimeout(() => {
                    this.scrollToSection()
                }, 500)
            }
        },

        beforeDestroy () {
            clearTimeout(this.timeout)
        },

        watch: {
            $route: {
                handler: function (val, oldVal) {
                    if (_.get(val, 'hash') === '#enrol') {
                        this.timeout = setTimeout(() => {
                            this.scrollToSection('#enrol')
                        }, 1000)
                    } else {
                        this.scrollToSection()
                    }
                }
            }
        }
    }
</script>

<style lang="scss">
    .gh-dmhc-dialog {
        .container {
            width: 100%;
        }
        .el-dialog__body {
            padding-top: 0;
        }

        .gh-dmhc-dialog__heading {
            word-break: keep-all;
            font-size: 1.5rem;
        }

        .gh-dmhc-dialog__subheading {
            word-break: keep-all;
            font-size: 18px;
            margin: 1rem 0;
        }
    }
</style>
